import type { Sender } from "../adamo/actions";
import { Choice, Slider, TextSetting } from "./Controls";
import type { PathState, Settings } from "../adamo/types";
import { SETTING_LIMITS } from "../adamo/types";

/** The path's points can only be set where the arm is being driven by hand. */
export const CAN_TEACH = ["teach", "pick"];

export function SettingsForm({ settings, path, change, sender, disabled, state }: {
  settings: Settings;
  path: PathState;
  change: <K extends keyof Settings>(key: K, value: Settings[K], final?: boolean) => void;
  sender: Sender;
  disabled: boolean;
  state: string | null;
}) {
  const teaching = state !== null && CAN_TEACH.includes(state);
  // Only ever disabled because somebody else is driving. Whether a point can be set right now
  // is the arm's call, and it refuses in a sentence the page shows.
  const noPoints = disabled;
  return (
    <>
      <h2>Settings</h2>
      {settings.task !== undefined && settings.task !== null && (
        <TextSetting label="Task (what the episodes are recorded as — name what is in the box)"
                     value={settings.task} placeholder="pick up one chess piece" disabled={disabled}
                     onSave={(v) => change("task", v)} />
      )}
      <Slider label="Default gripper width" value={settings.gripper_open} {...SETTING_LIMITS.gripper_open}
              format={(v) => `${Math.round(v * 100)}%`} disabled={disabled}
              onInput={(v) => change("gripper_open", v, false)}
              onCommit={(v) => change("gripper_open", v)} />
      <Slider label="Arm movement speed" value={settings.speed} {...SETTING_LIMITS.speed}
              format={(v) => `${Math.round(v * 1000)} mm/s`} disabled={disabled}
              onInput={(v) => change("speed", v, false)}
              onCommit={(v) => change("speed", v)} />
      <Slider label="Gripper close speed" value={settings.grip_close_speed} {...SETTING_LIMITS.grip_close_speed}
              format={(v) => (v <= 0 ? "at once" : `${(1 / v).toFixed(1)} s to shut`)} disabled={disabled}
              onInput={(v) => change("grip_close_speed", v, false)}
              onCommit={(v) => change("grip_close_speed", v)} />
      <Choice label="Control type" value={settings.mode} disabled={disabled}
              options={[{ value: "crane", label: "Joysticks" }, { value: "pose", label: "Motion" }]}
              onChange={(v) => change("mode", v)} />
      <Choice label="Depth cues on the picture" value={settings.overlay ? "on" : "off"} disabled={disabled}
              options={[{ value: "on", label: "Shown" }, { value: "off", label: "Hidden" }]}
              onChange={(v) => change("overlay", v === "on")} />
      {settings.mode === "pose" && (
        <Choice label="Dominant arm" value={settings.hand} disabled={disabled}
                options={[{ value: "left", label: "Left" }, { value: "right", label: "Right" }]}
                onChange={(v) => change("hand", v)} />
      )}

      <h2>Waypoints</h2>
      <p className="note">
        start {path.start ? "set" : "unset"} · {path.waypoints} point{path.waypoints === 1 ? "" : "s"} · end {path.end ? "set" : "unset"}
        {!teaching && " — a point is set where the gripper is, so the arm has to be enabled and being driven"}
      </p>
      <div className="buttons">
        <button disabled={noPoints} onClick={() => sender.send("set_start")}>Set start</button>
        <button disabled={noPoints || !path.start} onClick={() => sender.send("add_waypoint")}>Add path point</button>
        <button disabled={noPoints || !path.start} onClick={() => sender.send("set_end")}>Set end</button>
        <button disabled={noPoints} onClick={() => sender.send("undo")}>Undo</button>
        <button className="warn" disabled={noPoints} onClick={() => sender.send("clear_path")}>Clear</button>
        <button className="primary" disabled={disabled || state !== "teach" || !path.start || !path.end}
                onClick={() => sender.send("go")}>Go</button>
      </div>
    </>
  );
}
